<template>
  <div class="p-8 bg-[#FAFAFA] min-h-screen font-sans text-[#222222]">
    <header class="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
      <div>
        <h1 class="text-3xl font-extrabold tracking-tight">{{ t.title }}</h1>
        <p class="text-gray-500 font-medium">{{ t.subtitle }}</p>
      </div>
      <button @click="showAddModal = true" class="bg-blue-500 text-white px-6 py-3 rounded-2xl font-bold text-sm shadow-lg shadow-blue-200 hover:scale-105 transition-transform flex items-center gap-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 4v16m8-8H4" /></svg>
        {{ t.newClient }}
      </button>
    </header>

    <div class="bg-white p-4 rounded-[2rem] shadow-sm border border-gray-100 mb-8 flex flex-wrap gap-4 items-center">
      <div class="flex-1 min-w-[300px] relative">
        <span class="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input type="text" :placeholder="t.searchPlaceholder" class="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-dimotec-orange/20 transition-all text-sm">
      </div>
    </div>

    <div class="bg-white rounded-[2.5rem] shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
      <template v-if="clients.length === 0">
        <div class="p-10 text-center text-gray-400 font-bold">
          🚀 {{ t.noAgency }}
        </div>
      </template>

      <template v-else>
        <table class="w-full border-collapse">
          <thead>
            <tr class="bg-gray-50/50 text-left text-[11px] font-black uppercase tracking-[0.15em] text-gray-400 border-b border-gray-100">
              <th class="px-8 py-6">{{ t.clientEmail }}</th>
              <th class="px-6 py-6 text-center">{{ t.kiosksApk }}</th>
              <th class="px-6 py-6 text-center">{{ t.screensTV }}</th>
              <th class="px-6 py-6">{{ t.systemStatus }}</th>
              <th class="px-8 py-6 text-right">{{ t.actions }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="client in clients" :key="client.id" class="hover:bg-gray-50/50 transition-colors group">
              <td class="px-8 py-6">
                <div class="flex items-center gap-4">
                  <div class="w-12 h-12 bg-[#222222] rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-inner uppercase">
                    {{ client.nom.charAt(0) }}
                  </div>
                  <div>
                    <p class="font-bold text-gray-900 group-hover:text-dimotec-orange transition-colors">{{ client.nom }}</p>
                    <p class="text-xs text-gray-400 font-mono">{{ client.siret }}</p>
                  </div>
                </div>
              </td>
              <td class="px-6 py-6">
                <div class="flex flex-col items-center">
                  <span class="text-sm font-black text-gray-800">{{ client.bornesActives }} / {{ client.bornesTotal }}</span>
                  <div class="w-16 h-1 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                    <div class="h-full bg-blue-500 transition-all" :style="{ width: (client.bornesActives / client.bornesTotal * 100) + '%' }"></div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-6 text-center">
                <span class="px-3 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-bold border border-purple-100">
                  {{ client.ecrans }} {{ t.screens }}
                </span>
              </td>
              <td class="px-6 py-6">
                <div :class="statusClasses(client.status)" class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border">
                  {{ client.statusLabel }}
                </div>
              </td>
              <td class="px-8 py-6 text-right">
                <button @click="openConfig(client)" class="p-2.5 text-gray-400 hover:text-dimotec-orange hover:bg-blue-50 rounded-xl transition-all" title="Paramétrer">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37a1.724 1.724 0 002.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </div>

    <div v-if="showAddModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-white w-full max-w-xl rounded-[2.5rem] p-10 shadow-2xl">
        <h2 class="text-2xl font-black mb-2">{{ t.addClient }}</h2>
        <p class="text-gray-500 mb-8 text-sm font-medium">{{ t.addClientSubtitle }}</p>
        <div class="space-y-4">
          <input v-model="newClient.nom" type="text" :placeholder="t.companyName" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20">
          <input v-model="newClient.email" type="email" :placeholder="t.emailContact" class="w-full bg-gray-50 border-none rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20">
          <div class="grid grid-cols-2 gap-4">
            <input v-model.number="newClient.bornesTotal" type="number" :placeholder="t.nbKiosks" class="bg-gray-50 border-none rounded-xl py-3 px-4">
            <input v-model.number="newClient.ecrans" type="number" :placeholder="t.nbScreens" class="bg-gray-50 border-none rounded-xl py-3 px-4">
          </div>
        </div>
        <div class="flex gap-4 mt-8">
          <button @click="showAddModal = false" class="flex-1 py-3 font-bold text-gray-400">{{ t.cancel }}</button>
          <button @click="saveNewClient" class="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold shadow-lg shadow-blue-100">{{ t.createAccount }}</button>
        </div>
      </div>
    </div>

    <div v-if="selectedClient" class="fixed inset-0 z-50 overflow-hidden">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-[2px]" @click="selectedClient = null"></div>
      <div class="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl p-10 overflow-y-auto rounded-l-[3rem] animate-in slide-in-from-right duration-300">
        
        <div class="flex justify-between items-start mb-8">
          <div>
            <span class="text-[10px] font-black uppercase text-dimotec-orange tracking-widest">{{ t.masterConfig }}</span>
            <h2 class="text-3xl font-black">{{ selectedClient.nom }}</h2>
          </div>
          <button @click="selectedClient = null" class="p-2 bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div class="space-y-8">
          <section class="bg-gray-50/50 p-6 rounded-3xl border border-gray-100 space-y-4">
            <h3 class="text-xs font-black uppercase tracking-widest text-gray-400">{{ t.identityQuotas }}</h3>
            <div>
              <label class="block text-[10px] font-bold text-gray-400 ml-1 mb-1 uppercase">{{ t.socialReason }}</label>
              <input v-model="selectedClient.nom" type="text" class="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20 font-bold">
            </div>
            <div>
              <label class="block text-[10px] font-bold text-gray-400 ml-1 mb-1 uppercase">{{ t.emailSiret }}</label>
              <input v-model="selectedClient.siret" type="text" class="w-full bg-white border border-gray-200 rounded-xl py-3 px-4 focus:ring-2 focus:ring-dimotec-orange/20">
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-[10px] font-bold text-gray-400 ml-1 mb-1 uppercase">{{ t.maxKiosks }}</label>
                <input v-model.number="selectedClient.bornesTotal" type="number" class="w-full bg-white border border-gray-200 rounded-xl py-3 px-4">
              </div>
              <div>
                <label class="block text-[10px] font-bold text-gray-400 ml-1 mb-1 uppercase">{{ t.maxScreens }}</label>
                <input v-model.number="selectedClient.ecrans" type="number" class="w-full bg-white border border-gray-200 rounded-xl py-3 px-4">
              </div>
            </div>

            <div class="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 shadow-sm mt-4">
              <span class="text-xs font-black uppercase text-gray-700">{{ t.systemAccess }}</span>
              <button @click="toggleStatus(selectedClient)" 
                :class="selectedClient.status === 'active' ? 'bg-green-500' : 'bg-gray-300'" 
                class="w-14 h-7 relative rounded-full transition-all duration-300">
                <span :class="selectedClient.status === 'active' ? 'translate-x-7' : 'translate-x-1'" class="absolute top-1 left-0 w-5 h-5 bg-white rounded-full shadow transition-transform"></span>
              </button>
            </div>
          </section>


          <section>
  <h3 class="text-xs font-black uppercase tracking-widest text-gray-400 mb-4">{{ t.devicesQueues }}</h3>

    <!-- SCREEN -->
  <div class="mb-6">
    <h4 class="text-[10px] font-black text-gray-500 uppercase mb-2">{{ t.screensTv }}</h4>
    <div v-for="screen in selectedClient.devices.filter(d => d.type === 'SCREEN')" :key="screen._id" class="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 mb-2">
      <span class="px-3 py-1 bg-purple-50 text-purple-600 rounded-lg text-xs font-bold border border-purple-100">{{ screen.name }}</span>
      <code class="flex-1 text-[11px] font-mono text-gray-500 truncate overflow-hidden">{{ screen.connectionUrl }}</code>
      <button @click="copyLink(screen.connectionUrl)" class="text-xs font-black text-dimotec-orange">{{ t.copy }}</button>
    </div>
  </div>

  <!-- KIOSK -->
  <div class="mb-6">
    <h4 class="text-[10px] font-black text-gray-500 uppercase mb-2">{{ t.kiosksKiosk }}</h4>
    <div v-for="kiosk in selectedClient.devices.filter(d => d.type === 'KIOSK')" :key="kiosk._id" class="flex items-center justify-between p-4 border-2 border-dashed border-gray-100 rounded-2xl mb-2">
      <div>
        <p class="text-xs font-black text-gray-900">{{ kiosk.name }}</p>
        <p class="text-[10px] text-gray-400 font-mono">ID: {{ kiosk.identifier }}</p>
      </div>
      <button @click="showKioskKey(kiosk)" class="bg-[#222222] text-white text-[10px] font-black px-3 py-1.5 rounded-lg uppercase hover:bg-black transition-colors">
        {{ t.viewKey }}
      </button>
    </div>
  </div>



  <!-- Queues -->
  <div class="mb-6">
    <h4 class="text-[10px] font-black text-gray-500 uppercase mb-2">{{ t.queues }}</h4>
    <div v-for="queue in selectedClient.queues" :key="queue._id" class="flex items-center gap-2 p-3 bg-gray-50 rounded-2xl border border-gray-100 mb-2">
      <span class="text-[10px] font-bold text-gray-500">{{ queue.prefix }}</span>
      <span class="text-sm font-black text-gray-900">{{ queue.name }}</span>
      <span v-if="queue.active" class="ml-auto text-green-500 text-[10px] font-black uppercase">{{ t.active }}</span>
      <span v-else class="ml-auto text-gray-400 text-[10px] font-black uppercase">{{ t.inactive }}</span>
    </div>
  </div>
</section>


          <div class="pt-6 border-t border-gray-100 flex flex-col gap-3">
            <button @click="updateClient(selectedClient)" class="w-full py-4 bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-blue-100 hover:brightness-110 transition-all">
              {{ t.saveSettings }}
            </button>
            <button @click="deleteClient(selectedClient.id)" class="w-full py-4 bg-red-50 text-red-500 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-red-100 transition-colors">
              {{ t.deleteAgency }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>
<script>
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
      showAddModal: false,
      selectedClient: null,
      UpdateselectedClient: null,
      newClient: { nom: '', siret: '', bornesTotal: 1, ecrans: 1 },
      clients: [], // on va remplir depuis le backend
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      translations: {
        fr: {
          title: "Portefeuille Clients",
          subtitle: "Gestion des licences et des infrastructures déployées.",
          newClient: "Nouveau Client",
          searchPlaceholder: "Rechercher un client, une ville, un SIRET...",
          noAgency: "Aucune agence configurée pour le moment",
          clientEmail: "Client / Email",
          kiosksApk: "Bornes APK",
          screensTV: "Écrans TV",
          systemStatus: "Statut Système",
          actions: "Actions",
          screens: "Écrans",
          configure: "Paramétrer",
          addClient: "Ajouter un Client",
          addClientSubtitle: "Configurez les informations légales pour démarrer l'infrastructure.",
          companyName: "Raison sociale",
          emailContact: "Email contact",
          nbKiosks: "Nb Bornes",
          nbScreens: "Nb Écrans",
          cancel: "Annuler",
          createAccount: "Créer le compte",
          masterConfig: "Configuration Master",
          identityQuotas: "Identité & Quotas",
          socialReason: "Raison Sociale",
          emailSiret: "Email / SIRET",
          maxKiosks: "Bornes Max",
          maxScreens: "Écrans Max",
          systemAccess: "Accès Système",
          devicesQueues: "Devices et Fils d'Attente",
          screensTv: "Écrans TV",
          copy: "COPIER",
          kiosksKiosk: "Bornes KIOSK",
          viewKey: "Voir clé",
          queues: "Files d'attente",
          active: "Active",
          inactive: "Inactive",
          saveSettings: "Sauvegarder les Paramètres",
          deleteAgency: "Supprimer l'agence du réseau",
          operational: "Opérationnel",
          suspended: "Suspendu"
        },
        en: {
          title: "Client Portfolio",
          subtitle: "License management and deployed infrastructure.",
          newClient: "New Client",
          searchPlaceholder: "Search for a client, city, SIRET...",
          noAgency: "No agency configured at the moment",
          clientEmail: "Client / Email",
          kiosksApk: "APK Kiosks",
          screensTV: "TV Screens",
          systemStatus: "System Status",
          actions: "Actions",
          screens: "Screens",
          configure: "Configure",
          addClient: "Add Client",
          addClientSubtitle: "Configure legal information to start the infrastructure.",
          companyName: "Company name",
          emailContact: "Contact email",
          nbKiosks: "Nb Kiosks",
          nbScreens: "Nb Screens",
          cancel: "Cancel",
          createAccount: "Create account",
          masterConfig: "Master Configuration",
          identityQuotas: "Identity & Quotas",
          socialReason: "Company Name",
          emailSiret: "Email / SIRET",
          maxKiosks: "Max Kiosks",
          maxScreens: "Max Screens",
          systemAccess: "System Access",
          devicesQueues: "Devices and Queues",
          screensTv: "TV Screens",
          copy: "COPY",
          kiosksKiosk: "KIOSK Terminals",
          viewKey: "View key",
          queues: "Queues",
          active: "Active",
          inactive: "Inactive",
          saveSettings: "Save Settings",
          deleteAgency: "Delete agency from network",
          operational: "Operational",
          suspended: "Suspended"
        },
        de: {
          title: "Kundenportfolio",
          subtitle: "Lizenzverwaltung und bereitgestellte Infrastruktur.",
          newClient: "Neuer Kunde",
          searchPlaceholder: "Nach Kunde, Stadt, SIRET suchen...",
          noAgency: "Derzeit keine Agentur konfiguriert",
          clientEmail: "Kunde / E-Mail",
          kiosksApk: "APK-Kioske",
          screensTV: "TV-Bildschirme",
          systemStatus: "Systemstatus",
          actions: "Aktionen",
          screens: "Bildschirme",
          configure: "Konfigurieren",
          addClient: "Kunde hinzufügen",
          addClientSubtitle: "Rechtsinformationen konfigurieren, um die Infrastruktur zu starten.",
          companyName: "Firmenname",
          emailContact: "Kontakt-E-Mail",
          nbKiosks: "Anz. Kioske",
          nbScreens: "Anz. Bildschirme",
          cancel: "Abbrechen",
          createAccount: "Konto erstellen",
          masterConfig: "Master-Konfiguration",
          identityQuotas: "Identität & Kontingente",
          socialReason: "Firmenname",
          emailSiret: "E-Mail / SIRET",
          maxKiosks: "Max. Kioske",
          maxScreens: "Max. Bildschirme",
          systemAccess: "Systemzugriff",
          devicesQueues: "Geräte und Warteschlangen",
          screensTv: "TV-Bildschirme",
          copy: "KOPIEREN",
          kiosksKiosk: "KIOSK-Terminals",
          viewKey: "Schlüssel anzeigen",
          queues: "Warteschlangen",
          active: "Aktiv",
          inactive: "Inaktiv",
          saveSettings: "Einstellungen speichern",
          deleteAgency: "Agentur aus Netzwerk entfernen",
          operational: "Betriebsbereit",
          suspended: "Ausgesetzt"
        },
        it: {
          title: "Portfolio Clienti",
          subtitle: "Gestione licenze e infrastruttura distribuita.",
          newClient: "Nuovo Cliente",
          searchPlaceholder: "Cerca cliente, città, SIRET...",
          noAgency: "Nessuna agenzia configurata al momento",
          clientEmail: "Cliente / Email",
          kiosksApk: "Chioschi APK",
          screensTV: "Schermi TV",
          systemStatus: "Stato Sistema",
          actions: "Azioni",
          screens: "Schermi",
          configure: "Configura",
          addClient: "Aggiungi Cliente",
          addClientSubtitle: "Configura le informazioni legali per avviare l'infrastruttura.",
          companyName: "Ragione sociale",
          emailContact: "Email contatto",
          nbKiosks: "N. Chioschi",
          nbScreens: "N. Schermi",
          cancel: "Annulla",
          createAccount: "Crea account",
          masterConfig: "Configurazione Master",
          identityQuotas: "Identità & Quote",
          socialReason: "Ragione Sociale",
          emailSiret: "Email / SIRET",
          maxKiosks: "Max Chioschi",
          maxScreens: "Max Schermi",
          systemAccess: "Accesso Sistema",
          devicesQueues: "Dispositivi e Code",
          screensTv: "Schermi TV",
          copy: "COPIA",
          kiosksKiosk: "Terminali KIOSK",
          viewKey: "Visualizza chiave",
          queues: "Code",
          active: "Attiva",
          inactive: "Inattiva",
          saveSettings: "Salva Impostazioni",
          deleteAgency: "Elimina agenzia dalla rete",
          operational: "Operativo",
          suspended: "Sospeso"
        }
      }
    };
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    }
  },
  mounted() {
    this.fetchAgencies();
  },
  methods: {
    showKioskKey(kiosk) {
    // Affiche un alert ou modal avec le token et URL
    alert(`Clé du Kiosk ${kiosk.name} :\n${kiosk.connectionToken}\nURL : ${kiosk.connectionUrl}`);
    // Copier automatiquement dans le presse-papier
    navigator.clipboard.writeText(kiosk.connectionUrl).then(() => {
      toast.success(`URL copiée pour ${kiosk.name}`);
    });
  },

async fetchAgencies() {
  try {
    const token = localStorage.getItem("superAdminToken");
    const res = await fetch("https://api.datafuse.fr/api/super-admin/agencies", {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!res.ok) {
      const errorData = await res.json();
      toast.error(errorData.error || "Impossible de récupérer les agences");
      return;
    }

    const data = await res.json();
    console.log(data);

    // Transformer les agences pour correspondre au format frontend
    this.clients = data.map(agency => ({
      id: agency._id,
      nom: agency.name,
      siret: agency.email, 
      bornesTotal: agency.devices.filter(d => d.type === "KIOSK").length,
      bornesActives: agency.devices.filter(d => d.type === "KIOSK" && d.online).length,
      ecrans: agency.devices.filter(d => d.type === "SCREEN").length,
      devices: agency.devices, // pour modal avec KIOSK / SCREEN
      queues: agency.queues,   // pour modal avec files d'attente
      files: agency.queues.length,
      status: agency.status.toLowerCase(),
      statusLabel: agency.status === "ACTIVE" ? "Opérationnel" : "Suspendu"
    }));

  } catch (err) {
    console.error(err);
    toast.error("Erreur serveur lors de la récupération des agences");
  }
},


    toggleStatus(client) {
      client.status = client.status === 'active' ? 'suspended' : 'active';
      client.statusLabel = client.status === 'active' ? 'Opérationnel' : 'Suspendu';
    },

    statusClasses(status) {
      switch(status) {
        case 'actif': return 'bg-green-50 text-green-600 border-green-100';
        case 'maintenance': return 'bg-red-50 text-red-600 border-red-100';
        default: return 'bg-blue-50 text-blue-600 border-blue-100';
      }
    },

    openConfig(client) {
      this.selectedClient = JSON.parse(JSON.stringify(client)); // Deep copy
    },


  async saveNewClient() {
    try {
      const token = localStorage.getItem("superAdminToken");
      const body = {
        name: this.newClient.nom,
        email: this.newClient.email,  // ici on utilise un vrai email
        kiosksCount: this.newClient.bornesTotal,
        screensCount: this.newClient.ecrans,
        status: "ACTIVE"
      };

      const res = await fetch("https://api.datafuse.fr/api/super-admin/agency", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errorData = await res.json();
        toast.error(errorData.error || "Erreur lors de la création de l'agence");
        return;
      }

      const data = await res.json();

      // Ajouter l'agence créée dans le tableau pour l'affichage
      this.clients.push({
        id: data.agency._id,
        nom: data.agency.name,
        siret: data.agency.email,
        bornesTotal: data.agency.kiosksCount,
        bornesActives: 0,
        ecrans: data.agency.screensCount,
        files: 0,
        status: data.agency.status.toLowerCase(),
        statusLabel: data.agency.status === "ACTIVE" ? "Opérationnel" : "Suspendu"
      });

      toast.success("Agence créée avec succès !");
      this.showAddModal = false;
      this.newClient = { nom: '', email: '', bornesTotal: 1, ecrans: 1 };

    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur lors de la création de l'agence");
    }
  },

  // Modifier une agence
  async updateClient(client) {
    try {
      const token = localStorage.getItem("superAdminToken");
      const body = {
        name: client.nom,
        email: client.siret,       // ton backend attend "email"
        kiosksCount: client.bornesTotal,
        screensCount: client.ecrans,
        status: client.status.toUpperCase()
      };

      const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${client.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        const errorData = await res.json();
        toast.error(errorData.error || "Erreur lors de la modification de l'agence");
        return;
      }

      const data = await res.json();
      toast.success("Agence modifiée avec succès !");
      
      // Met à jour l'agence dans le tableau local
      const index = this.clients.findIndex(c => c.id === client.id);
      if (index !== -1) this.clients.splice(index, 1, {
        id: data.agency._id,
        nom: data.agency.name,
        siret: data.agency.email,
        bornesTotal: data.agency.kiosksCount,
        bornesActives: client.bornesActives, // conserve l'état actuel
        ecrans: data.agency.screensCount,
        files: client.files,
        status: data.agency.status.toLowerCase(),
        statusLabel: data.agency.status === "ACTIVE" ? "Opérationnel" : "Suspendu"
      });

      this.selectedClient = null; // ferme le modal de config
    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur lors de la modification de l'agence");
    }
  },

  // Supprimer une agence
  async deleteClient(clientId) {
    try {
      const token = localStorage.getItem("superAdminToken");

      const confirmDelete = confirm("Êtes-vous sûr de vouloir supprimer cette agence ?");
      if (!confirmDelete) return;

      const res = await fetch(`https://api.datafuse.fr/api/super-admin/agency/${clientId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const errorData = await res.json();
        toast.error(errorData.error || "Erreur lors de la suppression de l'agence");
        return;
      }

      toast.success("Agence supprimée avec succès !");
      this.clients = this.clients.filter(c => c.id !== clientId);

    } catch (err) {
      console.error(err);
      toast.error("Erreur serveur lors de la suppression de l'agence");
    }
  },

    copyLink() {
      alert("Lien dynamique copié !");
    }
  }
}
</script>
